import React, { useEffect, useRef, useState } from 'react'
import { ArrowTopRightOnSquareIcon, CheckIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { opcjeSprzetu, szukajMagazynow } from '../../api/oferta'
import { szukajModeli } from '../../api/falowniki'
import { modeleProducenta } from '../../api/modeleKatalogu'
import { ProductImage } from '../ProductImage'
import Rozwijacz from '../oferta/Rozwijacz'
import { modelZNazwy } from '../../lib/pme2/teczka'
import { krotkaNazwaSprzetu } from '../../lib/klienci/handel'
import { buildModelUrl } from '../../utils/modelUrl'

/**
 * Serie i konkretne modele obok marek w preferencjach doboru (prośba właściciela 2026-10-09:
 * „ktoś będzie chciał konkretne modele czy serie”). Wartość: `{ serie: [{producent, seria}],
 * modele: [{id, nazwa, producent, img}] }`, zapisywana w `KitPreferences.wybrane[kategoria]`.
 *
 * Serie z tej samej listy co filtr serii w „Sprawdź ofertę” (`GET /api/sprawdz_oferte/opcje`),
 * więc silnik doboru liczy je tak samo.
 *
 * Runda 5 (2026-10-12): zamiast zwiniętej sekcji z systemowymi listami niebieski przycisk „Dodaj model”.
 * Otwiera panel jak szybki dobór w karcie klienta (`klienci/SzybkiDobor`): wyszukiwarka z lupą albo
 * wybór producent, seria, model na listach z wyszukiwaniem (`oferta/Rozwijacz`). Wybrana seria może
 * wejść w całości („Dodaj całą serię”). Wybrane modele prowadzą do strony modelu w nowej karcie,
 * bo zmiany w preferencjach nie są jeszcze zapisane.
 */

// Kategoria preferencji -> nazwa kategorii w API „Sprawdź ofertę”, wyszukiwarce modeli,
// `ProductImage` i adresie strony modelu (`/model/<kategoria>/<id>`).
const KATEGORIA_API = { inverter: 'inwerter', battery: 'magazyn', panels: 'panel' }
const JEDNOSTKA = { inverter: 'kW', battery: 'kWh', panels: 'W' }

const POLE = 'w-full rounded-lg border-2 border-blue-200 bg-blue-50/40 px-3 py-2 text-sm focus:border-blue-500 focus:bg-white focus:outline-none'
const liczba = (x) => String(x).replace('.', ',')

/** „1 oferta”, „3 oferty”, „5 ofert”. */
const ofert = (n) => {
  if (n === 1) return '1 oferta'
  const j = n % 10, d = n % 100
  return j >= 2 && j <= 4 && (d < 12 || d > 14) ? `${n} oferty` : `${n} ofert`
}

/**
 * Sam model, bez „Falownik hybrydowy” i producenta z nazwy sklepowej. Najpierw separatory „ - ”
 * (krotkaNazwaSprzetu), bo inaczej z „Moduł bateryjny - DEYE - SE-G5.1 PRO-B - 5,15 kWh - LV”
 * zostawały same kreski wokół modelu.
 */
export const nazwaModelu = (nazwa, producent) => {
  const model = modelZNazwy(krotkaNazwaSprzetu(nazwa, 500), producent).replace(/^[\s\-–:,]+|[\s\-–:,]+$/g, '')
  return krotkaNazwaSprzetu(model || nazwa)
}

/** Adres strony modelu z zapisanego wyboru (`{id}`); strona czyta tylko id na końcu. */
export const adresModelu = (kategoria, model) => buildModelUrl(KATEGORIA_API[kategoria], { id: model?.id })

/** Logo producenta albo jego inicjał, gdy pliku z logo nie ma (jak w kafelkach marek). */
const LogoProducenta = ({ producent, logoPath }) => {
  const [padlo, setPadlo] = useState(false)
  const src = logoPath && producent ? logoPath(producent) : null
  if (!src || padlo) {
    return <span className="text-sm font-bold text-blue-600">{String(producent || '?').slice(0, 1).toUpperCase()}</span>
  }
  return <img src={src} alt="" className="h-full w-full object-contain" onError={() => setPadlo(true)} />
}

/**
 * Kafelek wybranej serii albo modelu w stylu kafelków marek z logo (`ChipWyboru` w preferencjach):
 * prostokąt z ramką, obrazek po lewej, dwie linie tekstu, usuwanie krzyżykiem w rogu po najechaniu.
 * Z `adres` obrazek i nazwa otwierają stronę modelu w nowej karcie.
 */
const Kafelek = ({ obraz, nadpis, tytul, pelnaNazwa, adres, onUsun }) => {
  const ramkaObrazu = 'flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-100 bg-white p-1'
  return (
    <span className="group relative inline-flex h-14 w-72 max-w-full items-center gap-2.5 rounded-lg border border-gray-200 bg-white
                     py-1.5 pl-1.5 pr-3 transition-colors hover:border-blue-300">
      {adres
        ? <a href={adres} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden="true" className={`${ramkaObrazu} hover:border-blue-300`}>{obraz}</a>
        : <span className={ramkaObrazu}>{obraz}</span>}
      <span className="min-w-0">
        <span className="block truncate text-[11px] text-gray-500">{nadpis}</span>
        {adres ? (
          <a href={adres} target="_blank" rel="noopener noreferrer" title={`${pelnaNazwa || tytul}: strona modelu w nowej karcie`}
             className="flex min-w-0 items-center gap-1 text-sm font-semibold text-gray-900 hover:text-blue-700">
            <span className="truncate">{tytul}</span>
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
          </a>
        ) : (
          <span className="block truncate text-sm font-semibold text-gray-900" title={pelnaNazwa || tytul}>{tytul}</span>
        )}
      </span>
      <button type="button" onClick={onUsun} aria-label={`Usuń ${tytul}`}
              className="absolute -right-2 -top-2 rounded-full border border-gray-300 bg-white p-0.5 text-gray-500 opacity-0 shadow-sm
                         transition-opacity hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100">
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    </span>
  )
}

/** Wyszukiwarka modeli z lupą i listą podpowiedzi ze zdjęciem (wzór: `WyborProduktu` w `SzybkiDobor`). */
const SzukajModelu = ({ kategoria, przyklad, wybraneId, onWybor }) => {
  const kat = KATEGORIA_API[kategoria]
  const [fraza, setFraza] = useState('')
  const [wyniki, setWyniki] = useState([])
  const [szuka, setSzuka] = useState(false)
  const ostatnie = useRef(0)
  const pole = useRef(null)
  const gotowa = fraza.trim().length >= 2

  // Panel otwiera się po kliknięciu „Dodaj model”, więc kursor od razu w polu.
  useEffect(() => { pole.current?.focus({ preventScroll: true }) }, [])

  useEffect(() => {
    if (!gotowa) { setWyniki([]); return undefined }
    const t = setTimeout(async () => {
      const moje = ++ostatnie.current
      setSzuka(true)
      const r = kat === 'magazyn'
        ? await szukajMagazynow(fraza.trim())
        : ((await szukajModeli(kat, { q: fraza.trim() }).catch(() => ({ wyniki: [] }))).wyniki || [])
      if (moje === ostatnie.current) { setWyniki(r); setSzuka(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [fraza, gotowa, kat])

  const wybierz = (w) => { setFraza(''); setWyniki([]); onWybor(w) }

  return (
    <div className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input ref={pole} value={fraza} onChange={(e) => setFraza(e.target.value)} placeholder={`Szukaj modelu, np. ${przyklad}`}
             aria-label="Szukaj modelu" className={`${POLE} pl-9`} />
      {gotowa && (
        <div className="absolute z-30 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
          {szuka && <p className="px-2 py-2 text-xs text-gray-500">Szukam…</p>}
          {!szuka && wyniki.length === 0 && <p className="px-2 py-2 text-xs text-gray-500">Nie ma takiego modelu w katalogu.</p>}
          {!szuka && wyniki.map((w) => {
            const juz = wybraneId.includes(w.model_id)
            return (
              <button key={w.model_id} type="button" disabled={juz} onClick={() => wybierz(w)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-blue-50 disabled:cursor-default disabled:hover:bg-transparent">
                <ProductImage src={w.img_url} alt="" productType={kat} className="h-8 w-8 flex-shrink-0 rounded object-contain" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-gray-900" title={w.nazwa}>{krotkaNazwaSprzetu(w.nazwa)}</span>
                  <span className="block text-[11px] text-gray-500">
                    {[w.producent, w.moc ? `${liczba(w.moc)} ${JEDNOSTKA[kategoria]}` : '', w.ofert ? ofert(w.ofert) : ''].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {juz && <span className="inline-flex flex-shrink-0 items-center gap-1 text-[11px] font-semibold text-blue-700"><CheckIcon className="h-3.5 w-3.5" /> dodany</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Podpis nad listą z wyszukiwaniem (bez wielkich liter w etykiecie `Rozwijacz`). */
const Pole = ({ etykieta, children }) => (
  <div>
    <p className="mb-1 text-[11px] text-gray-500">{etykieta}</p>
    {children}
  </div>
)

/** Panel „Dodaj model”: wyszukiwarka albo producent, seria, model. */
const PanelDodawania = ({ kategoria, producenci, przyklad, serie, modele, onDodajModel, onDodajSerie, onZamknij }) => {
  const kat = KATEGORIA_API[kategoria]
  const [producent, setProducent] = useState('')
  const [listaSerii, setListaSerii] = useState([])
  const [seria, setSeria] = useState('')
  const [listaModeli, setListaModeli] = useState(null)

  useEffect(() => {
    setSeria('')
    if (!producent) { setListaSerii([]); return undefined }
    let aktualne = true
    opcjeSprzetu(kat, producent).then((o) => { if (aktualne) setListaSerii(o?.serie || []) })
    return () => { aktualne = false }
  }, [kat, producent])

  // Modele producenta, po wyborze serii tylko z tej serii. `null` = jeszcze się wczytują.
  useEffect(() => {
    if (!producent) { setListaModeli(null); return undefined }
    let aktualne = true
    setListaModeli(null)
    modeleProducenta(kategoria, producent, seria).then((l) => { if (aktualne) setListaModeli(l) })
    return () => { aktualne = false }
  }, [kategoria, producent, seria])

  const wybraneId = modele.map((m) => m.id)
  const doWyboru = (listaModeli || []).filter((m) => !wybraneId.includes(m.model_id))
  const seriaJuz = serie.some((s) => s.producent === producent && s.seria === seria)
  const placeholderModelu = !producent ? 'najpierw producent'
    : listaModeli === null ? 'Wczytuję…'
      : doWyboru.length === 0 ? (listaModeli.length ? 'wszystkie już dodane' : 'brak modeli z ceną') : `Wybierz (${doWyboru.length})`

  return (
    <div className="mt-3 rounded-xl border-2 border-blue-100 bg-blue-50/30 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 sm:max-w-md">
          <SzukajModelu kategoria={kategoria} przyklad={przyklad} wybraneId={wybraneId} onWybor={onDodajModel} />
        </div>
        <button type="button" onClick={onZamknij} aria-label="Zamknij dodawanie modelu"
                className="ml-auto rounded-lg p-2 text-gray-400 hover:bg-white hover:text-gray-700">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="my-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-blue-100" />
        <span className="text-xs text-gray-500">albo wybierz z listy</span>
        <span className="h-px flex-1 bg-blue-100" />
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <Pole etykieta="Producent">
          <Rozwijacz ariaLabel="Producent" placeholder="Wybierz" szukajPlaceholder="Szukaj producenta" wartosc={producent}
                     opcje={producenci.map((p) => ({ value: p, label: p }))} onZmiana={setProducent} />
        </Pole>
        <Pole etykieta="Seria">
          <Rozwijacz ariaLabel="Seria" pusta="Wszystkie serie" szukajPlaceholder="Szukaj serii" wartosc={seria}
                     placeholder={producent && listaSerii.length === 0 ? 'brak serii w katalogu' : 'Wszystkie serie'}
                     disabled={!producent || listaSerii.length === 0}
                     opcje={listaSerii.map((s) => ({ value: s.value, label: s.value, licznik: s.count }))} onZmiana={setSeria} />
        </Pole>
        <Pole etykieta="Model">
          {/* Wybór od razu dodaje model, a lista zostaje na tym samym producencie i serii:
              zwykle dodaje się kilka mocy z jednej serii. */}
          <Rozwijacz ariaLabel="Model" placeholder={placeholderModelu} szukajPlaceholder="Szukaj modelu albo mocy" wartosc=""
                     disabled={doWyboru.length === 0}
                     opcje={doWyboru.map((m) => ({
                       value: m.model_id, label: nazwaModelu(m.nazwa, m.producent),
                       licznik: m.moc ? `${liczba(m.moc)} ${JEDNOSTKA[kategoria]}` : null,
                     }))}
                     onZmiana={(v) => { const m = doWyboru.find((x) => String(x.model_id) === String(v)); if (m) onDodajModel(m) }} />
        </Pole>
      </div>

      {producent && seria && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <button type="button" onClick={() => onDodajSerie(producent, seria)} disabled={seriaJuz}
                  className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700
                             hover:border-blue-400 disabled:opacity-50">
            {seriaJuz ? <CheckIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
            {seriaJuz ? `Seria ${seria} dodana` : `Dodaj całą serię ${seria}`}
          </button>
          {!seriaJuz && <span className="text-[11px] text-gray-500">Cała seria obejmie też modele, które pojawią się w katalogu później.</span>}
        </div>
      )}
    </div>
  )
}

const WyborSeriiIModeli = ({ kategoria, producenci, wartosc, onChange, przyklad, logoPath }) => {
  const serie = wartosc?.serie || []
  const modele = wartosc?.modele || []
  const ile = serie.length + modele.length
  const [otwarte, setOtwarte] = useState(false)

  const zmien = (zmiany) => onChange({ serie, modele, ...zmiany })
  const dodajSerie = (producent, seria) => {
    if (!producent || !seria || serie.some((s) => s.producent === producent && s.seria === seria)) return
    zmien({ serie: [...serie, { producent, seria }] })
  }
  const dodajModel = (w) => {
    if (modele.some((m) => m.id === w.model_id)) return
    // Zdjęcie tylko z adresu http(s): zastępcze obrazki `data:` z katalogu nie mają sensu w zapisie.
    const img = /^https?:\/\//.test(w.img_url || '') ? w.img_url : ''
    zmien({ modele: [...modele, { id: w.model_id, nazwa: w.nazwa, producent: w.producent || '', img }] })
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      {ile > 0 && (
        <>
          <p className="text-xs font-semibold text-gray-700">Wybrane serie i modele ({ile})</p>
          <div className="mb-3 mt-2 flex flex-wrap gap-2.5">
            {serie.map((s) => (
              <Kafelek key={`${s.producent}|${s.seria}`} nadpis={`${s.producent}, seria`} tytul={s.seria}
                       obraz={<LogoProducenta producent={s.producent} logoPath={logoPath} />}
                       onUsun={() => zmien({ serie: serie.filter((x) => x !== s) })} />
            ))}
            {modele.map((m) => (
              <Kafelek key={m.id} nadpis={m.producent || 'model'} tytul={nazwaModelu(m.nazwa, m.producent)} pelnaNazwa={m.nazwa}
                       adres={adresModelu(kategoria, m)}
                       obraz={<ProductImage src={m.img || null} alt="" productType={KATEGORIA_API[kategoria]} className="h-full w-full object-contain" />}
                       onUsun={() => zmien({ modele: modele.filter((x) => x.id !== m.id) })} />
            ))}
          </div>
        </>
      )}

      {otwarte ? (
        <PanelDodawania kategoria={kategoria} producenci={producenci} przyklad={przyklad} serie={serie} modele={modele}
                        onDodajModel={dodajModel} onDodajSerie={dodajSerie} onZamknij={() => setOtwarte(false)} />
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button type="button" onClick={() => setOtwarte(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm
                             hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
            <PlusIcon className="h-4 w-4" /> Dodaj model
          </button>
          {ile === 0 && <span className="text-xs text-gray-500">Konkretny model albo całą serię, gdy marka to za mało.</span>}
        </div>
      )}
    </div>
  )
}

export default WyborSeriiIModeli
